import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import type { ImportPlatformProductsMutation } from '~/graphql/graphql';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';

export type ImportResult =
  ImportPlatformProductsMutation['importPlatformProducts'];

const NAMESPACE = 'product-import';
const PAGE_SIZE = 50;

function useRevealedRows(rows: TableRow[]) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const showMore = useCallback(
    () => setVisibleCount((count) => count + PAGE_SIZE),
    []
  );
  const { sentinelRef, reset } = useInfiniteScroll({
    hasMore: visibleCount < rows.length,
    isLoading: false,
    onLoadMore: showMore,
  });
  useEffect(() => reset(), [visibleCount, reset]);

  return { visibleRows: rows.slice(0, visibleCount), sentinelRef };
}

export function FileErrors({
  errors,
}: Readonly<{ errors: ImportResult['fileErrors'] }>) {
  const { t } = useTranslation(NAMESPACE);

  return (
    <div data-testid="product-import-file-errors">
      <SectionPanel title={t('fileErrors.title')}>
        <ul className="list-disc pl-5 text-sm text-error-600">
          {errors.map((message, index) => (
            <li key={`${index}-${message}`}>{message}</li>
          ))}
        </ul>
      </SectionPanel>
    </div>
  );
}

export function RowErrors({
  errors,
}: Readonly<{ errors: ImportResult['rowErrors'] }>) {
  const { t } = useTranslation(NAMESPACE);
  const tableControls = useTable({});

  const columns: TableColumn[] = [
    { key: 'rowNumber', header: t('table.row'), width: 6 },
    { key: 'code', header: t('table.code'), width: 14 },
    { key: 'messages', header: t('table.messages'), width: 60 },
  ];
  const rows: TableRow[] = errors.map((error, index) => ({
    id: { value: String(index) },
    rowNumber: { value: String(error.rowNumber) },
    code: { value: error.code },
    messages: { value: error.messages.join('; ') },
  }));
  const { visibleRows, sentinelRef } = useRevealedRows(rows);

  return (
    <div data-testid="product-import-row-errors">
      <SectionPanel title={t('rowErrors.title')} bodyClassName="p-0">
        <Table
          minWidth={60}
          rows={visibleRows}
          columns={columns}
          tableControls={tableControls}
        />
        <div ref={sentinelRef} />
      </SectionPanel>
    </div>
  );
}

export function Changes({
  products,
}: Readonly<{ products: ImportResult['products'] }>) {
  const { t } = useTranslation(NAMESPACE);
  const tableControls = useTable({});

  const columns: TableColumn[] = [
    { key: 'code', header: t('table.code'), width: 12 },
    { key: 'action', header: t('table.action'), width: 9 },
    { key: 'createdFields', header: t('table.createdFields'), width: 20 },
    { key: 'addedLengths', header: t('table.addedLengths'), width: 14 },
    { key: 'addedCapacities', header: t('table.addedCapacities'), width: 20 },
    { key: 'conflicts', header: t('table.conflicts'), width: 25 },
  ];
  const rows: TableRow[] = products.map((product) => ({
    id: { value: product.code },
    code: { value: product.code },
    action: { value: t(`action.${product.action}`) },
    createdFields: {
      value: product.createdFields
        .map((field) => `${field.field}: ${field.value}`)
        .join('; '),
    },
    addedLengths: { value: product.addedLengths.join(', ') },
    addedCapacities: { value: product.addedCapacities.join('; ') },
    conflicts: {
      value: product.conflicts
        .map(
          (conflict) =>
            `${conflict.field}: ${conflict.currentValue} → ${conflict.importedValue}`
        )
        .join('; '),
    },
  }));
  const { visibleRows, sentinelRef } = useRevealedRows(rows);

  return (
    <div data-testid="product-import-changes">
      <SectionPanel title={t('changes.title')} bodyClassName="p-0">
        <Table
          minWidth={80}
          rows={visibleRows}
          columns={columns}
          tableControls={tableControls}
        />
        <div ref={sentinelRef} />
      </SectionPanel>
    </div>
  );
}
